/**
 * pmChartOptions.js : jeux de données et options Chart.js du graphique PM de
 * la page de session (pur). Extrait de PMChart.jsx (audit E.5) : le composant
 * garde le rendu et les interactions, la configuration vit ici.
 */
import { THRESHOLDS } from './chartAnnotations';
import { formatDateLocale } from '../utils/formatters';

// Couleurs des séries : turquoise (PM1), violet (PM2.5), magenta (PM10).
const SERIES_STYLE = {
  pm1: { label: 'PM1', field: 'pm1', borderWidth: 1.5, pointHoverRadius: 4, fill: false,
    light: ['rgba(0, 206, 209, 1)', 'rgba(0, 206, 209, 0.1)'], dark: ['rgba(64, 224, 208, 0.9)', 'rgba(64, 224, 208, 0.1)'] },
  pm25: { label: 'PM2.5', field: 'pm2_5', borderWidth: 2.5, pointHoverRadius: 5, fill: true,
    light: ['rgba(147, 112, 219, 1)', 'rgba(147, 112, 219, 0.15)'], dark: ['rgba(186, 135, 255, 0.9)', 'rgba(186, 135, 255, 0.15)'] },
  pm10: { label: 'PM10', field: 'pm10', borderWidth: 2, pointHoverRadius: 4, fill: false,
    light: ['rgba(255, 20, 147, 1)', 'rgba(255, 20, 147, 0.1)'], dark: ['rgba(255, 105, 180, 0.9)', 'rgba(255, 105, 180, 0.1)'] },
};

/** Min / moyenne / max d'une série, valeurs nulles ou négatives ignorées (panneau du graphique). */
function seriesStats(values) {
  let min = Infinity, max = -Infinity, sum = 0, count = 0;
  for (const v of values) {
    if (v == null || v <= 0) continue;
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
    count++;
  }
  return count === 0 ? { min: 0, max: 0, avg: 0 } : { min, max, avg: sum / count };
}

/** Statistiques du panneau au-dessus du graphique, par grandeur. */
export function pmPanelStats(readings) {
  const r = readings || [];
  return {
    pm1Stats: seriesStats(r.map(x => x.pm1)),
    pm25Stats: seriesStats(r.map(x => x.pm2_5)),
    pm10Stats: seriesStats(r.map(x => x.pm10)),
  };
}

/** Échelle Y adaptative : monte jusqu'au prochain seuil PM2.5 pertinent. */
export function pmYAxisMax(readings) {
  const maxPM = Math.max(...readings.flatMap(r => [r.pm1 || 0, r.pm2_5 || 0, r.pm10 || 0]));
  const nextThreshold = [THRESHOLDS.pm25.good, THRESHOLDS.pm25.moderate, THRESHOLDS.pm25.bad]
    .find(t => t > maxPM);
  return Math.max(
    nextThreshold ? Math.min(nextThreshold * 1.15, maxPM * 2) : maxPM * 1.3,
    maxPM * 1.2,
    5
  );
}

/** Données du graphique ; une grandeur sans donnée réelle (tout à zéro) n'a pas de série. */
export function buildPmData(readings, visible, darkMode) {
  return {
    // Label d'axe X (affichage) : heure « HH:MM » localisée (FR/EN identiques, colon).
    labels: readings.map(r => formatDateLocale(r.timestamp, { hour: '2-digit', minute: '2-digit' })),
    datasets: Object.entries(SERIES_STYLE)
      .filter(([key]) => visible[key])
      .map(([, s]) => {
        const [borderColor, backgroundColor] = darkMode ? s.dark : s.light;
        return {
          label: s.label, data: readings.map(r => r[s.field]),
          borderColor, backgroundColor, borderWidth: s.borderWidth,
          pointRadius: 0, pointHoverRadius: s.pointHoverRadius, fill: s.fill, tension: 0.3,
        };
      }),
  };
}

// Info seuil d'une valeur PM2.5 dans l'infobulle — échelle unique alerts.level.* (1.3a).
function pm25ThresholdInfo(value, t) {
  if (value <= THRESHOLDS.pm25.good) return ` ✓ ${t('alerts.level.good')}`;
  if (value <= THRESHOLDS.pm25.moderate) {
    return ` ${t('charts.tooltip.delta', { d: (value - THRESHOLDS.pm25.good).toFixed(1) })}`;
  }
  if (value <= THRESHOLDS.pm25.bad) {
    return ` ${t('charts.tooltip.deltaOver', { d: (value - THRESHOLDS.pm25.moderate).toFixed(1) })}`;
  }
  return ` · ${t('alerts.level.high')}`;
}

/**
 * Options Chart.js.
 * @param {{ readings, annotations: Object, yAxisMax: number, zoom: Object, darkMode: boolean, t: Function }} p
 *   `annotations` : annotations Chart.js déjà construites ; `zoom` : options
 *   du plugin de zoom (useChartZoomWindow).
 */
export function buildPmOptions({ readings, annotations, yAxisMax, zoom, darkMode, t }) {
  const grid = { color: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)', drawBorder: false };
  const tickColor = darkMode ? '#E8EDF2' : '#4A5568';
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: { color: darkMode ? '#E1E1E1' : '#2C3E50', font: { size: 12, weight: '600' }, usePointStyle: true, padding: 15 },
        onClick: (e, legendItem, legend) => {
          const index = legendItem.datasetIndex;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);
          // Toggle visibility
          meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
          chart.update();
        },
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(26, 26, 46, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: darkMode ? '#E1E1E1' : '#2C3E50',
        bodyColor: tickColor,
        borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(77, 166, 255, 0.5)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            const datasetLabel = context.dataset.label;
            const thresholdInfo = datasetLabel === 'PM2.5' ? pm25ThresholdInfo(value, t) : '';
            return `${datasetLabel}: ${value.toFixed(1)} µg/m³${thresholdInfo}`;
          },
          footer: (tooltipItems) => {
            // Afficher le temps au format lisible
            const timestamp = readings[tooltipItems[0].dataIndex]?.timestamp;
            return timestamp
              ? t('charts.tooltip.time', { time: formatDateLocale(timestamp, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) })
              : '';
          },
        },
      },
      annotation: { annotations },
      zoom,
    },
    scales: {
      x: { grid, ticks: { color: tickColor, font: { size: 11 }, maxTicksLimit: 20 } },
      y: {
        beginAtZero: true,
        max: yAxisMax, // Utilise la valeur pré-calculée
        grid,
        ticks: { color: tickColor, font: { size: 11 } },
        title: { display: true, text: t('charts.axis.concentration'), color: tickColor, font: { size: 12, weight: '600' } },
      },
    },
  };
}
